import React, { useEffect, useRef, useState } from "react";
import useStore from "../../../../context/useStore";
import { clientService } from "../../../../services/clientService";
import documentsAdapters from "../../../../adapters/documentsAdapters";
import tools from "../../../../helpers/tools";
import Loader from "../../../common/Loader";
import CustomPagination from "../../../common/CustomPagination";
import LicensePlate from "../../../common/LicensePlate/LicensePlate";
import SelectSearch from "../../../common/SelectSearch";
import TagInput from "../../../common/TagInput";
import useDocuments from "../hooks/useDocuments";
import styles from "../Documents.module.scss";

// Un document rattaché au devis couvre toutes les immatriculations issues de ce
// devis (`quantity` > 1 → plusieurs contrats), un document rattaché à la demande
// couvre tous ses devis. Dans les deux cas il apparaît sous chaque
// immatriculation concernée : l'infobulle l'explique au lieu de laisser croire
// à un doublon.
const EXPLICATION_PORTEE = {
  devis:
    "Document rattaché au devis : il apparaît sous chaque immatriculation issue de ce devis.",
  demande:
    "Document rattaché à la demande de financement : il apparaît sous chaque immatriculation de la demande.",
};

/**
 * LS-693 — liste des documents d'un véhicule, tous modules confondus.
 *
 * Un seul composant pour les deux points d'entrée prévus par la carte : le menu
 * global (`filtresEtendus`) et l'encart de la fiche véhicule de flotte, où
 * l'immatriculation est imposée via `filtresVerrouilles` et les colonnes
 * redondantes masquées.
 *
 * Lecture seule : ni dépôt ni suppression ici, ils restent dans les modules
 * d'origine qui portent leurs propres règles de verrouillage.
 */
const DocumentsPanel = ({
  filtresVerrouilles = {},
  filtresEtendus = false,
  messageVide = "Aucun document trouvé.",
}) => {
  const [state] = useStore();
  const [clients, setClients] = useState([]);
  // Déjà chargés pour toute route privée par CatalogStoreMiddleware.
  const catalogues = state.catalogStore?.items || [];

  const {
    filters,
    updateFilter,
    resetFilters,
    setPage,
    documents,
    meta,
    isLoading,
    hasError,
    aDejaCharge,
    recharger,
    selection,
    toggleSelection,
    togglePageEntiere,
    telechargerArchive,
    isArchiving,
    maxSelection,
  } = useDocuments({ filtresVerrouilles });

  const typeOptions = documentsAdapters.typeOptionsFromConstants(
    state.constant.items,
  );

  const immatVerrouillee =
    "immatriculation" in filtresVerrouilles ||
    "immatriculations" in filtresVerrouilles;

  // Le filtre client de l'API attend un uuid, pas un nom : la liste complète
  // alimente un SelectSearch, qui filtre en local (même usage que l'export
  // clients et le simulateur de rachat).
  useEffect(() => {
    if (!filtresEtendus) return;
    clientService
      .getAllClients()
      .then((res) => setClients(res.data || []))
      .catch(() => setClients([]));
  }, [filtresEtendus]);

  const pageEntiereSelectionnee =
    documents.length > 0 &&
    documents.every((doc) => selection.includes(doc.uuid));

  // L'état natif « indéterminé » n'existe qu'en propriété DOM, pas en attribut.
  const caseEnteteRef = useRef(null);
  const pagePartiellementSelectionnee =
    !pageEntiereSelectionnee &&
    documents.some((doc) => selection.includes(doc.uuid));
  useEffect(() => {
    if (caseEnteteRef.current) {
      caseEnteteRef.current.indeterminate = pagePartiellementSelectionnee;
    }
  }, [pagePartiellementSelectionnee]);

  const { desactive: archiveIndisponible, avertissement: avertissementArchive } =
    documentsAdapters.etatArchive({
      nbSelection: selection.length,
      total: meta.total,
      maxSelection,
      isArchiving,
    });

  // Seuls les filtres éditables comptent : les verrous (immat de la fiche
  // véhicule) ne sont pas dans `filters` et ne déclenchent pas la proposition
  // de réinitialisation.
  const filtresRenseignes = Object.values(filters).some((valeur) =>
    Array.isArray(valeur) ? valeur.length > 0 : valeur !== "",
  );

  const nbColonnes = filtresEtendus ? 11 : 7;

  return (
    <div className={styles.panel}>
      <div className={styles.filterBar}>
        {!immatVerrouillee && (
          <input
            className={styles.filterInput}
            placeholder="Immatriculation (partielle : AB-123-CD, ab123, 123…)"
            aria-label="Filtrer par immatriculation"
            value={filters.immatriculation}
            onChange={(e) => updateFilter("immatriculation", e.target.value)}
          />
        )}

        <select
          className={styles.filterSelect}
          aria-label="Filtrer par source"
          value={filters.source}
          onChange={(e) => updateFilter("source", e.target.value)}
        >
          <option value="">Toutes les sources</option>
          {documentsAdapters.SOURCE_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          aria-label="Filtrer par type de document"
          value={filters.type}
          onChange={(e) => updateFilter("type", e.target.value)}
        >
          <option value="">Tous les types</option>
          {typeOptions.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        {filtresEtendus && (
          <>
            <input
              className={styles.filterInput}
              placeholder="Référence devis…"
              aria-label="Filtrer par référence de devis"
              value={filters.devisReference}
              onChange={(e) => updateFilter("devisReference", e.target.value)}
            />
            <input
              className={styles.filterInput}
              placeholder="N° de dossier…"
              aria-label="Filtrer par numéro de dossier"
              value={filters.numeroDossier}
              onChange={(e) => updateFilter("numeroDossier", e.target.value)}
            />
          </>
        )}

        {/* Deux champs date nus ne disent pas ce qu'ils filtrent : le libellé
            « Dépôt du … au » est visible, pas seulement en infobulle. */}
        <div
          className={styles.filterDates}
          role="group"
          aria-label="Période de dépôt"
        >
          <span className={styles.filterDatesLabel}>Dépôt du</span>
          <input
            type="date"
            className={styles.filterDate}
            aria-label="Déposé à partir du"
            value={filters.dateDebut}
            onChange={(e) => updateFilter("dateDebut", e.target.value)}
          />
          <span className={styles.filterDatesLabel}>au</span>
          <input
            type="date"
            className={styles.filterDate}
            aria-label="Déposé jusqu'au"
            value={filters.dateFin}
            onChange={(e) => updateFilter("dateFin", e.target.value)}
          />
        </div>

        <button
          className={styles.filterReset}
          onClick={resetFilters}
          title="Réinitialiser les filtres"
          aria-label="Réinitialiser les filtres"
        >
          <i className="fa fa-times" />
        </button>
      </div>

      {filtresEtendus && (
        <div className={styles.filterBarSecondaire}>
          <div className={styles.filterField}>
            <SelectSearch
              name="clientUuid"
              label="Client"
              datas={clients}
              dataIndex="uuid"
              dataLabel="name"
              value={filters.clientUuid}
              placeholder="Tous les clients"
              change={(e) => updateFilter("clientUuid", e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            <SelectSearch
              name="catalogUuid"
              label="Catalogue"
              datas={catalogues}
              dataIndex="uuid"
              dataLabel="name"
              value={filters.catalogUuid}
              placeholder="Tous les catalogues"
              change={(e) => updateFilter("catalogUuid", e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            {/* Liste d'immatriculations exactes : prépare une archive
                multi-véhicules sans passer par la recherche partielle. */}
            <TagInput
              name="immatriculations"
              label="Immatriculations (liste exacte)"
              placeholder="Ajouter une immatriculation…"
              value={filters.immatriculations}
              onChange={(e) => updateFilter("immatriculations", e.target.value)}
              tagClassName="fs-6"
            />
          </div>
        </div>
      )}

      <div className={styles.actionBar}>
        <span className={styles.resultCount}>
          {!hasError && (
            <>
              {meta.total} document{meta.total > 1 ? "s" : ""}
              {selection.length > 0 &&
                ` · ${selection.length} sélectionné${
                  selection.length > 1 ? "s" : ""
                }`}
            </>
          )}
        </span>
        <div className={styles.actionBarRight}>
          {avertissementArchive && (
            <span className={styles.warning}>{avertissementArchive}</span>
          )}
          <button
            className={styles.btnArchive}
            onClick={telechargerArchive}
            disabled={archiveIndisponible}
            title={
              selection.length > 0
                ? "Télécharger les documents sélectionnés"
                : "Télécharger tous les documents du résultat filtré, toutes pages confondues"
            }
          >
            <i
              className={`fa fa-${isArchiving ? "spinner fa-spin" : "file-archive-o"}`}
              style={{ marginRight: 6 }}
            />
            {selection.length > 0
              ? `Télécharger la sélection (${selection.length})`
              : "Tout télécharger (ZIP)"}
          </button>
        </div>
      </div>

      {isLoading && !aDejaCharge ? (
        <Loader />
      ) : (
        <>
          <div
            className={`${styles.tableWrapper} ${
              isLoading ? styles.enChargement : ""
            }`}
            aria-busy={isLoading}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.cellCheck}>
                    <input
                      type="checkbox"
                      ref={caseEnteteRef}
                      checked={pageEntiereSelectionnee}
                      onChange={togglePageEntiere}
                      disabled={documents.length === 0}
                      title="Sélectionner les documents de cette page"
                      aria-label="Sélectionner les documents de cette page"
                    />
                  </th>
                  {filtresEtendus && <th>Immatriculation</th>}
                  {filtresEtendus && <th>Client</th>}
                  {filtresEtendus && <th>Catalogue</th>}
                  <th>Source</th>
                  <th>Type</th>
                  <th>Fichier</th>
                  <th>Portée</th>
                  <th>Devis</th>
                  {filtresEtendus && <th>N° dossier</th>}
                  <th>Déposé le</th>
                </tr>
              </thead>
              <tbody>
                {hasError && (
                  <tr>
                    <td colSpan={nbColonnes} className={styles.empty}>
                      Le chargement des documents a échoué.
                      <button className={styles.emptyAction} onClick={recharger}>
                        Réessayer
                      </button>
                    </td>
                  </tr>
                )}
                {!hasError && documents.length === 0 && (
                  <tr>
                    <td colSpan={nbColonnes} className={styles.empty}>
                      {/* Avec des filtres saisis, « aucun document » parle des
                          filtres — pas du véhicule ni du module. */}
                      {filtresRenseignes
                        ? "Aucun document ne correspond aux filtres."
                        : messageVide}
                      {filtresRenseignes && (
                        <button
                          className={styles.emptyAction}
                          onClick={resetFilters}
                        >
                          Réinitialiser les filtres
                        </button>
                      )}
                    </td>
                  </tr>
                )}
                {/* Un document porté par un devis multi-véhicules sort une
                    ligne par immatriculation, toutes avec le même uuid de
                    media : la clé compose donc immat et devis pour rester
                    unique. La sélection, elle, reste par uuid — cocher une
                    ligne coche ses jumelles, c'est le même fichier. */}
                {documents.map((doc) => (
                  <tr
                    key={`${doc.uuid}|${doc.immatriculation || ""}|${
                      doc.devis?.uuid || ""
                    }`}
                  >
                    <td className={styles.cellCheck}>
                      <input
                        type="checkbox"
                        checked={selection.includes(doc.uuid)}
                        onChange={() => toggleSelection(doc.uuid)}
                        aria-label={`Sélectionner ${doc.fileName}`}
                      />
                    </td>
                    {filtresEtendus && (
                      <td>
                        {doc.immatriculation ? (
                          <LicensePlate value={doc.immatriculation} size="sm" />
                        ) : (
                          <span
                            className={styles.muted}
                            title="Document déposé sur un devis sans contrat : l'immatriculation n'existe pas encore."
                          >
                            —
                          </span>
                        )}
                      </td>
                    )}
                    {filtresEtendus && <td>{doc.client?.nom || "—"}</td>}
                    {filtresEtendus && <td>{doc.catalogue?.nom || "—"}</td>}
                    <td>{doc.source?.libelle || "—"}</td>
                    <td>{doc.type?.libelle || "—"}</td>
                    <td>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.fileLink}
                      >
                        <i
                          className={`fa fa-${documentsAdapters.iconePourMime(
                            doc.mimeType,
                          )}`}
                          style={{ marginRight: 6 }}
                        />
                        {doc.fileName}
                      </a>
                      <span className={styles.fileSize}>
                        {documentsAdapters.formatTaille(doc.size)}
                      </span>
                    </td>
                    <td>
                      {doc.portePar ? (
                        <span
                          className={styles.badgePortee}
                          title={EXPLICATION_PORTEE[doc.portePar.valeur]}
                        >
                          {doc.portePar.libelle}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className={styles.cellDevis}>
                      {/* Nouvel onglet, comme le fichier : la liste filtrée
                          reste en place (ses filtres ne survivent pas à une
                          navigation). La visibilité du dossier lui-même reste
                          jugée par sa page. */}
                      {doc.devis?.dossierUuid ? (
                        <a
                          href={`/quotations/${doc.devis.dossierUuid}`}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.devisLink}
                          title="Ouvrir le dossier du devis"
                        >
                          {doc.devis.reference}
                        </a>
                      ) : (
                        doc.devis?.reference || "—"
                      )}
                    </td>
                    {filtresEtendus && <td>{doc.numeroDossier || "—"}</td>}
                    <td>{tools.formatDate(doc.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <CustomPagination
            total={meta.total}
            activePage={meta.current_page}
            handlePageParamsChange={({ page }) => setPage(page)}
          />
        </>
      )}
    </div>
  );
};

export default DocumentsPanel;
